import { _ } from "~/i18n";
import { IGetSendDataOutput, INewReportData, INewReportSendMetaData, IReportSendMetaData } from "~/report/interfaces";
import { LOCAL_USER } from "~/state/report/constants";
import { IReportSaveData, IReportData } from "~/state/report/interfaces";
import { saveReportData } from "~/state/report/state";
import { getId } from "~/state/report/utils";
import { store } from "~/state/store";

interface IItem {
	title: string;
	value?: string;
}

interface IDetailItem {
	date: string;
	items: Array<IItem>;
}

const orderedNames: Array<keyof IReportSaveData> = ["name", "location", "category", "opening", "openingPhotos", "www", "phone", "closed", "email", "note", "photos"];

function getCurDate() {
	const curDate = new Date();
	const month = (curDate.getMonth() + 1).toString().padStart(2, "0");
	const date = curDate.getDate().toString().padStart(2, "0");
	const hours = curDate.getHours().toString().padStart(2, "0");
	const minutes = curDate.getMinutes().toString().padStart(2, "0");
	const seconds = curDate.getSeconds().toString().padStart(2, "0");

	return `${curDate.getFullYear()}-${month}-${date} ${hours}:${minutes}:${seconds}`;
}

function getSaveData(data: IGetSendDataOutput, metaData: IReportSendMetaData) {
	const saveData: IReportSaveData = {
		date: getCurDate(),
		photos: metaData.hasPhotos,
	};
	const jsonData = data.noteData as IReportData;

	Object.keys(jsonData).forEach((key: keyof IReportData) => {
		switch (key) {
			// booleans
			case "closed":
				saveData[key] = jsonData[key];
				break;

			case "location":
				// @ts-ignore
				saveData.location = jsonData[key].address.trim();
				break;

			default:
				saveData[key] = jsonData[key];
		}
	});

	if (metaData.hasAttachPhotos) {
		saveData.openingPhotos = true;
	}

	return saveData;
}

function createItem(name: keyof IReportSaveData, data: IReportSaveData): null | IItem {
	let title = "";
	let value = (data[name] as string) || "";

	switch (name) {
		case "name":
			title = _("mymap.addPopup.name");
			break;

		case "location":
			title = _("report.localChanges.location");
			break;

		case "category":
			title = _("fixedSuggest.category2.title");
			break;

		case "opening":
			title = _("report.openingTime");
			break;

		case "www":
			title = _("search.visualizer.link2");
			break;

		case "phone":
			title = _("mobilePromo.form.phone");
			break;

		case "email":
			title = _("report.email");
			break;

		case "note":
			title = _("report.note");
			break;

		case "openingPhotos":
			title = _("report.openingTime");
			value = _("report.localChanges.addOpeningPhoto");
			break;

		case "photos":
			if (data.photos) {
				title = _("report.localChanges.addPlacePhotos");
				value = "";
			}
			break;

		case "closed":
			if (data.closed) {
				title = _("report.placeNotExist");
				value = "";
			}
			break;

		default:
	}

	if (!title) {
		return null;
	}

	return {
		title,
		...value
			? {
				value,
			}
			: {},
	};
}

function formatData(data: IReportSaveData) {
	const output: IDetailItem = {
		date: data.date,
		items: [],
	};

	for (const name of orderedNames) {
		const item = createItem(name, data);

		if (name in data && item) {
			output.items.push(item);
		}
	}

	return output;
}

export function getLocalChangesItems(source: string, id: number): IDetailItem | null {
	const storeState = store.getState();
	const rusId = storeState.user.user?.rusId || LOCAL_USER;
	const poiId = getId(source, id);
	const localChangesData = storeState.report.data;

	return rusId in localChangesData && localChangesData[rusId][poiId]
		? formatData(localChangesData[rusId][poiId])
		: null;
}

export function saveLocalChanges(data: IGetSendDataOutput, metaData: IReportSendMetaData) {
	// source & id + klikatko
	if (!data.mantisData.detailSource || !data.mantisData.detailId || !Mapy.CUS.userweb?.reportLocalChanges) {
		return;
	}

	// musim byt vzdycky prihlaseny
	const rusId = store.getState().user.user.rusId;
	const saveData = getSaveData(data, metaData);

	store.dispatch(saveReportData({
		rusId,
		source: data.mantisData.detailSource,
		id: data.mantisData.detailId,
		data: saveData,
	}));
}

export function saveLocalChangesNew(data: INewReportData, metaData: INewReportSendMetaData) {
	if (!Mapy.CUS.userweb?.reportLocalChanges) {
		return;
	}

	// musim byt vzdycky prihlaseny
	const rusId = store.getState().user.user.rusId;
	const { location, message, ...rest } = data.form_data;
	const savedData: IReportSaveData = {
		date: getCurDate(),
		...rest,
		...metaData.opening
			? {
				closed: true,
			}
			: {},
		...location?.address
			? {
				location: location.address,
			}
			: {},
	};

	if (metaData.hasPhotos) {
		savedData.photos = true;
	}

	if (metaData.hasAttachPhotos) {
		savedData.openingPhotos = true;
	}

	if (message) {
		savedData.note = message;
	}

	store.dispatch(saveReportData({
		rusId,
		source: metaData.source,
		id: metaData.id,
		data: savedData,
	}));
}
